const ALTURAS_AMOSTRADAS = 11;
function posicaoValida(posicao) {
    return (Array.isArray(posicao) &&
        posicao.length >= 2 &&
        Number.isFinite(posicao[0]) &&
        Number.isFinite(posicao[1]));
}
function pontoDe(posicao) {
    return [posicao[0], posicao[1]];
}
function aneisValidos(aneis) {
    return (aneis.length > 0 &&
        aneis.every((anel) => Array.isArray(anel) && anel.length >= 4 && anel.every(posicaoValida)));
}
function linhaValida(linha) {
    return Array.isArray(linha) && linha.length >= 2 && linha.every(posicaoValida);
}
function comprimento(linha) {
    let total = 0;
    for (let i = 1; i < linha.length; i += 1) {
        const [x0, y0] = pontoDe(linha[i - 1]);
        const [x1, y1] = pontoDe(linha[i]);
        total += Math.hypot(x1 - x0, y1 - y0);
    }
    return total;
}
/** Ponto na metade do comprimento acumulado da linha. */
function pontoMedioDaLinha(linha) {
    if (!linhaValida(linha)) {
        return null;
    }
    const metade = comprimento(linha) / 2;
    let acumulado = 0;
    for (let i = 1; i < linha.length; i += 1) {
        const [x0, y0] = pontoDe(linha[i - 1]);
        const [x1, y1] = pontoDe(linha[i]);
        const segmento = Math.hypot(x1 - x0, y1 - y0);
        if (segmento > 0 && acumulado + segmento >= metade) {
            const t = (metade - acumulado) / segmento;
            return [x0 + (x1 - x0) * t, y0 + (y1 - y0) * t];
        }
        acumulado += segmento;
    }
    return pontoDe(linha[0]);
}
function areaDoAnel(anel) {
    let soma = 0;
    for (let i = 1; i < anel.length; i += 1) {
        const [x0, y0] = pontoDe(anel[i - 1]);
        const [x1, y1] = pontoDe(anel[i]);
        soma += x0 * y1 - x1 * y0;
    }
    return Math.abs(soma) / 2;
}
/** Área planar aproximada, usada apenas para escolher o componente a rotular. */
function areaDoPoligono(aneis) {
    const [exterior, ...buracos] = aneis;
    if (exterior === undefined) {
        return 0;
    }
    return areaDoAnel(exterior) - buracos.reduce((soma, anel) => soma + areaDoAnel(anel), 0);
}
/**
 * Linha de varredura horizontal em 11 alturas dentro dos limites do anel exterior. As
 * interseções com todos os anéis, pareadas por paridade, formam os intervalos interiores
 * (buracos ficam de fora). Retorna o ponto médio do maior intervalo.
 */
function pontoInteriorDoPoligono(aneis) {
    if (!aneisValidos(aneis)) {
        return null;
    }
    const exterior = aneis[0];
    const ys = exterior.map((posicao) => posicao[1]);
    const minimoY = Math.min(...ys);
    const maximoY = Math.max(...ys);
    if (!(maximoY > minimoY)) {
        return null;
    }
    const centroY = (minimoY + maximoY) / 2;
    let melhor = null;
    for (let i = 1; i <= ALTURAS_AMOSTRADAS; i += 1) {
        const y = minimoY + ((maximoY - minimoY) * i) / (ALTURAS_AMOSTRADAS + 1);
        const xs = [];
        for (const anel of aneis) {
            for (let j = 1; j < anel.length; j += 1) {
                const [x0, y0] = pontoDe(anel[j - 1]);
                const [x1, y1] = pontoDe(anel[j]);
                if (y0 > y !== y1 > y) {
                    xs.push(x0 + ((y - y0) * (x1 - x0)) / (y1 - y0));
                }
            }
        }
        xs.sort((a, b) => a - b);
        for (let k = 0; k + 1 < xs.length; k += 2) {
            const inicio = xs[k];
            const fim = xs[k + 1];
            const largura = fim - inicio;
            const distancia = Math.abs(y - centroY);
            const maior = melhor === null ||
                largura > melhor.largura * (1 + 1e-9) ||
                (largura >= melhor.largura * (1 - 1e-9) && distancia < melhor.distancia);
            if (largura > 0 && maior) {
                melhor = { largura, ponto: [inicio + largura / 2, y], distancia };
            }
        }
    }
    return melhor === null ? null : melhor.ponto;
}
function pontoDoMultiPoligono(poligonos) {
    const candidatos = poligonos
        .filter((aneis) => aneisValidos(aneis))
        .map((aneis) => ({ aneis, area: areaDoPoligono(aneis) }))
        .sort((a, b) => b.area - a.area);
    const maior = candidatos[0];
    return maior === undefined ? null : pontoInteriorDoPoligono(maior.aneis);
}
function pontoDoMultiLinha(linhas) {
    const validas = linhas.filter(linhaValida).sort((a, b) => comprimento(b) - comprimento(a));
    const maior = validas[0];
    return maior === undefined ? null : pontoMedioDaLinha(maior);
}
/**
 * Um único ponto de rotulagem para a geometria, ou `null` quando não há posição válida.
 * Função pura: não altera a geometria recebida.
 */
export function obterPontoDoRotulo(geometria) {
    switch (geometria.type) {
        case 'Point':
            return posicaoValida(geometria.coordinates) ? pontoDe(geometria.coordinates) : null;
        case 'MultiPoint': {
            const primeiro = geometria.coordinates.find(posicaoValida);
            return primeiro === undefined ? null : pontoDe(primeiro);
        }
        case 'LineString':
            return pontoMedioDaLinha(geometria.coordinates);
        case 'MultiLineString':
            return pontoDoMultiLinha(geometria.coordinates);
        case 'Polygon':
            return pontoInteriorDoPoligono(geometria.coordinates);
        case 'MultiPolygon':
            return pontoDoMultiPoligono(geometria.coordinates);
        case 'GeometryCollection': {
            const prioridade = [
                ['Polygon', 'MultiPolygon'],
                ['LineString', 'MultiLineString'],
                ['Point', 'MultiPoint'],
            ];
            for (const tipos of prioridade) {
                for (const parte of geometria.geometries) {
                    if (tipos.includes(parte.type)) {
                        const ponto = obterPontoDoRotulo(parte);
                        if (ponto !== null) {
                            return ponto;
                        }
                    }
                }
            }
            return null;
        }
        default:
            return null;
    }
}
